class DeepPersonalizationService {
  constructor() {
    this.memoryCache = new Map();
    this.userProfiles = new Map();
    this.conversationMemory = new Map();
    this.lastUpdateTime = new Map();
    this.supabase = null;
  }

  // Initialize the service with supabase client
  initialize(supabaseClient) {
    this.supabase = supabaseClient;
  }

  // Get all user data from database tables
  async getAllUserData(userId) {
    try {
      if (!this.supabase) {
        console.error('Supabase client not initialized');
        return null;
      }

      // Validate and extract userId
      let validUserId = userId;
      if (typeof userId === 'object' && userId !== null) {
        // If userId is an object, try to extract the actual ID
        if (userId.id) {
          validUserId = userId.id;
        } else if (userId.user_id) {
          validUserId = userId.user_id;
        } else {
          console.error('Invalid userId object:', userId);
          return null;
        }
      }

      if (!validUserId || typeof validUserId !== 'string') {
        console.error('Invalid userId format:', { userId, validUserId, type: typeof validUserId });
        return null;
      }

      console.log('🔍 Fetching user data for ID:', validUserId);

      // Get user profile
      const { data: userProfile, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('*')
        .eq('id', validUserId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return null;
      }

      // Get recent conversations (last 50)
      const { data: conversations, error: conversationError } = await this.supabase
        .from('conversations')
        .select('*')
        .eq('user_id', validUserId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (conversationError) {
        console.error('Error fetching conversations:', conversationError);
      }

      // Get recent journal entries (last 30)
      const { data: journalEntries, error: journalError } = await this.supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', validUserId)
        .order('created_at', { ascending: false })
        .limit(30);

      if (journalError) {
        console.error('Error fetching journal entries:', journalError);
      }

      return {
        userProfile: userProfile || {},
        conversations: conversations || [],
        journalEntries: journalEntries || []
      };

    } catch (error) {
      console.error('Error in getAllUserData:', error);
      return null;
    }
  }

  // Build personal context from user data
  buildPersonalContext(userData) {
    if (!userData || !userData.userProfile) {
      return {
        personalMemory: '',
        relationshipContext: '',
        growthContext: '',
        preferences: {}
      };
    }

    const { userProfile, conversations, journalEntries } = userData;
    
    // Extract personal information
    const personalInfo = [];
    if (userProfile.full_name) personalInfo.push(`Name: ${userProfile.full_name}`);
    if (userProfile.location) personalInfo.push(`Location: ${userProfile.location}`);
    if (userProfile.occupation) personalInfo.push(`Occupation: ${userProfile.occupation}`);
    if (userProfile.age) personalInfo.push(`Age: ${userProfile.age}`);
    if (userProfile.interests && userProfile.interests.length > 0) {
      personalInfo.push(`Interests: ${userProfile.interests.join(', ')}`);
    }

    // Extract conversation themes and relationship building
    const relationshipInsights = this.analyzeRelationshipFromConversations(conversations);
    
    // Extract growth patterns from journal entries
    const growthInsights = this.analyzeGrowthFromJournal(journalEntries);

    // Extract preferences
    const preferences = {
      communication_style: userProfile.communication_style || 'supportive',
      goals: userProfile.goals || [],
      therapy_focus: userProfile.therapy_focus || [],
      voice_preference: userProfile.voice_preference || 'sarah'
    };

    return {
      personalMemory: personalInfo.join('\n'),
      relationshipContext: relationshipInsights,
      growthContext: growthInsights,
      preferences
    };
  }

  // Analyze relationship building from conversations
  analyzeRelationshipFromConversations(conversations) {
    if (!conversations || conversations.length === 0) {
      return 'New relationship - building initial trust and understanding.';
    }

    const insights = [];
    const recentConversations = conversations.slice(0, 10);
    
    // Analyze conversation frequency and engagement
    const conversationDates = recentConversations.map(c => new Date(c.created_at));
    const daysSinceLastChat = Math.floor((Date.now() - conversationDates[0]) / (1000 * 60 * 60 * 24));
    
    if (daysSinceLastChat === 0) {
      insights.push('Very engaged today - multiple conversations');
    } else if (daysSinceLastChat === 1) {
      insights.push('Regular daily engagement');
    } else if (daysSinceLastChat > 7) {
      insights.push(`Haven't connected in ${daysSinceLastChat} days - may need gentle re-engagement`);
    }

    // Analyze conversation topics and emotional themes
    const conversationTexts = recentConversations
      .map(c => c.user_input || c.ai_response || '')
      .join(' ')
      .toLowerCase();

    if (conversationTexts.includes('stress') || conversationTexts.includes('anxious')) {
      insights.push('Has been discussing stress and anxiety - needs supportive approach');
    }
    if (conversationTexts.includes('work') || conversationTexts.includes('job')) {
      insights.push('Work-related concerns are a recurring theme');
    }
    if (conversationTexts.includes('relationship') || conversationTexts.includes('family')) {
      insights.push('Personal relationships are important discussion topics');
    }

    return insights.length > 0 ? insights.join('. ') : 'Building understanding through ongoing conversations.';
  }

  // Analyze growth patterns from journal entries
  analyzeGrowthFromJournal(journalEntries) {
    if (!journalEntries || journalEntries.length === 0) {
      return 'No journal data available yet - encourage reflection and journaling.';
    }

    const insights = [];
    const recentEntries = journalEntries.slice(0, 10);
    
    // Analyze journaling frequency
    const journalDates = recentEntries.map(e => new Date(e.created_at));
    const daysSinceLastEntry = Math.floor((Date.now() - journalDates[0]) / (1000 * 60 * 60 * 24));
    
    if (daysSinceLastEntry === 0) {
      insights.push('Actively journaling today - great self-reflection habit');
    } else if (daysSinceLastEntry > 7) {
      insights.push('Journaling has lapsed - might benefit from gentle encouragement');
    }

    // Analyze emotional patterns in journal entries
    const journalTexts = recentEntries
      .map(e => e.content || e.entry || '')
      .join(' ')
      .toLowerCase();

    if (journalTexts.includes('grateful') || journalTexts.includes('thankful')) {
      insights.push('Shows gratitude practice in journaling');
    }
    if (journalTexts.includes('goal') || journalTexts.includes('progress')) {
      insights.push('Focused on personal goals and progress tracking');
    }
    if (journalTexts.includes('challenge') || journalTexts.includes('difficult')) {
      insights.push('Working through challenges - needs encouragement');
    }

    return insights.length > 0 ? insights.join('. ') : 'Beginning to explore self-reflection through journaling.';
  }

    // Create deep personalization prompt for AI
  async createDeepPersonalizationPrompt(userId, currentUserMessage = '') {
    try {
      // Get all user data
      const userData = await this.getAllUserData(userId);
      if (!userData) {
        return this.getDefaultPersonalizationPrompt(currentUserMessage);
      }

      // Build personal context
      const context = this.buildPersonalContext(userData);
      
      // Analyze the current message to understand what they're talking about
      const messageContext = this.analyzeCurrentMessage(currentUserMessage);
      
      // Create a natural, human-like prompt that feels like real relationship memory
      const prompt = `
You have a genuine relationship with this person. Here's what you know about them from your conversations:

THEIR PERSONAL STORY:
${context.personalMemory}

YOUR RELATIONSHIP WITH THEM:
${context.relationshipContext}

THEIR GROWTH JOURNEY:
${context.growthContext}

HOW THEY LIKE TO COMMUNICATE:
They prefer ${context.preferences.communication_style} conversations. Their main goals are: ${context.preferences.goals.join(', ') || 'exploring personal growth'}. They're focused on: ${context.preferences.therapy_focus.join(', ') || 'general wellness and life improvement'}.

WHAT THEY JUST SAID TO YOU:
"${currentUserMessage}"

${messageContext.analysis}

IMPORTANT CONVERSATION GUIDELINES:
- Honor and respect what they just shared: "${currentUserMessage}"
- Reference your genuine relationship and show you truly remember them
- ${messageContext.responseGuidance}
- Speak to them as an equal with deep respect for their wisdom and experience
- Respond to their emotional reality with authentic understanding
- Create a sense of partnership where they feel heard, valued, and respected
- Focus on understanding them deeply rather than rushing to give advice

Respond as someone who genuinely respects and cares about them. Show authentic interest in their wellbeing and honor their capacity for growth and healing.`;

      return prompt;
    } catch (error) {
      console.error('Error creating personalized prompt:', error);
      return this.getDefaultPersonalizationPrompt(currentUserMessage);
    }
  }

  // Analyze the current message to provide specific guidance
  analyzeCurrentMessage(message) {
    if (!message) {
      return {
        analysis: 'This appears to be the start of a new conversation.',
        responseGuidance: 'Focus on welcoming them warmly and inviting them to share'
      };
    }

    const msg = message.toLowerCase();
    
    // Detect emotional states
    if (msg.includes('anxious') || msg.includes('anxiety')) {
      return {
        analysis: 'They\'re sharing about anxiety - this requires careful, empathetic attention.',
        responseGuidance: 'Acknowledge their anxiety specifically, validate how difficult it is, and gently explore what\'s contributing to it'
      };
    }
    
    if (msg.includes('stressed') || msg.includes('stress')) {
      return {
        analysis: 'They\'re dealing with stress - they need understanding and practical support.',
        responseGuidance: 'Recognize the stress they\'re experiencing and ask about specific stressors or what\'s making things difficult'
      };
    }
    
    if (msg.includes('work') || msg.includes('job')) {
      return {
        analysis: 'Work-related concerns are being shared - this affects their daily wellbeing.',
        responseGuidance: 'Focus on the work situation they mentioned and how it\'s impacting them emotionally'
      };
    }
    
    if (msg.includes('relationship') || msg.includes('partner') || msg.includes('family')) {
      return {
        analysis: 'They\'re sharing about relationships - this is deeply personal and important.',
        responseGuidance: 'Show understanding about the relationship dynamics and explore their feelings about the situation'
      };
    }
    
    if (msg.includes('sad') || msg.includes('depressed') || msg.includes('down')) {
      return {
        analysis: 'They\'re expressing sadness or low mood - they need gentle support.',
        responseGuidance: 'Acknowledge their difficult feelings with warmth and explore what might be contributing to them feeling this way'
      };
    }
    
    return {
      analysis: 'They\'re sharing something important with you - give it your full attention.',
      responseGuidance: 'Listen carefully to what they\'re saying and respond to their specific experience with genuine interest and care'
    };
  }

  // Default prompt when personalization data is not available
  getDefaultPersonalizationPrompt(currentUserMessage = '') {
    if (!currentUserMessage) {
      return `
RELATIONSHIP CONTEXT: New conversation - focus on building rapport and understanding.
APPROACH: Be warm, welcoming, and focused on getting to know this person better.
GOAL: Create a safe space for open conversation and begin building trust.
`.trim();
    }

    const messageContext = this.analyzeCurrentMessage(currentUserMessage);
    
    return `
RELATIONSHIP CONTEXT: New relationship, but they just shared something important with you.
WHAT THEY JUST SAID: "${currentUserMessage}"
${messageContext.analysis}
APPROACH: ${messageContext.responseGuidance}
GOAL: Show that you're really listening and respond to what they actually shared.
`.trim();
  }

  // Cache management
  async refreshUserCache(userId) {
    try {
      const userData = await this.getAllUserData(userId);
      if (userData) {
        this.memoryCache.set(userId, userData);
        this.lastUpdateTime.set(userId, Date.now());
      }
    } catch (error) {
      console.error('Error refreshing user cache:', error);
    }
  }

  // Check if cache is stale (older than 5 minutes)
  isCacheStale(userId) {
    const lastUpdate = this.lastUpdateTime.get(userId);
    if (!lastUpdate) return true;
    
    const fiveMinutes = 5 * 60 * 1000;
    return (Date.now() - lastUpdate) > fiveMinutes;
  }

  // Get cached user data or fetch fresh
  async getCachedUserData(userId) {
    if (this.isCacheStale(userId)) {
      await this.refreshUserCache(userId);
    }
    return this.memoryCache.get(userId);
  }

  // Compatibility method for legacy code that expects buildComprehensiveMemory
  async buildComprehensiveMemory(userId) {
    try {
      console.log('🧠 Refreshing user memory cache for personalization');
      await this.refreshUserCache(userId);
      
      // Get user data for analysis
      const userData = await this.getAllUserData(userId);
      
      // Return structure compatible with existing UI code
      return {
        dataDepth: {
          conversations: userData?.conversations?.length || 0,
          journalEntries: userData?.journalEntries?.length || 0,
          totalInteractions: (userData?.conversations?.length || 0) + (userData?.journalEntries?.length || 0)
        },
        success: true
      };
    } catch (error) {
      console.error('Error building comprehensive memory:', error);
      return {
        dataDepth: {
          conversations: 0,
          journalEntries: 0,
          totalInteractions: 0
        },
        success: false
      };
    }
  }
}

// Export as singleton
export const deepPersonalizationService = new DeepPersonalizationService();
